import {
  buildPath,
  buildTemplatePath,
  CrudMode,
  Page,
  PageRoute,
  pn,
  Stack,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  Permission,
  StaffServiceType,
  StaffServiceTypeEditResource,
  StaffServiceTypeViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { BasicInfo } from "./BasicInfo";
import { StaffServiceTypePageHeader } from "./StaffServiceTypePageHeader";

const routeName = "staffServiceType";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

export const staffServiceTypePageRoute: PageRoute<Params> = {
  handle: "Staff Service Type",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const StaffServiceTypePage = () => {
  const ROOT_PERMISSION: Permission = Permission.SystemUtility;
  const navigate = useNavigate();
  const { params } = useParams(routeName, Params);
  const [serviceType, setServiceType] = useState<StaffServiceType>({} as StaffServiceType);
  const [initServiceType, setInitServiceType] = useState<StaffServiceType>(serviceType);
  const [viewResource, setViewResource] = useState<StaffServiceTypeViewResource>({});
  const [editResource, setEditResource] = useState<StaffServiceTypeEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [serviceType] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.staffServiceTypes.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") {
      ApiService.staffServiceTypes.getEditResource().then((response) => {
        setEditResource(response);
      });
    }
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${staffServiceTypePageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.staffServiceTypes.get(id).then((response) => {
      setServiceType(response);
      setInitServiceType(response);
      resetUnsavedChanges([response]);
    });
  };

  return (
    <Page
      isForm
      header={
        <StaffServiceTypePageHeader
          rootPermission={ROOT_PERMISSION}
          handle={staffServiceTypePageRoute.handle}
          crudMode={params.crudMode}
          reroute={reroute}
          fetch={fetch}
          serviceType={serviceType}
          setServiceType={setServiceType}
          initServiceType={initServiceType}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
          resetUnsavedChanges={resetUnsavedChanges}
        />
      }
    >
      <Stack spacing="lg">
        <BasicInfo
          crudMode={params.crudMode}
          serviceType={serviceType}
          setServiceType={setServiceType}
          viewResource={viewResource}
          editResource={editResource}
          validationErrors={validationErrors}
        />
      </Stack>
    </Page>
  );
};
